import { ArrowRight, Bot, FileText, Loader2, Send, Sparkles, UserRound } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  createGrowthAssessment,
  generateGrowthAssessmentReport,
  sendGrowthAssessmentMessage,
} from '../api.js'
import { useAuth } from '../auth-context.js'
import { setPageMetadata } from '../page-metadata.js'

function MessageBubble({ message }) {
  const isUser = message.role === 'user'
  return (
    <article className={`assessment-message ${isUser ? 'is-user' : 'is-assistant'}`}>
      <span>{isUser ? <UserRound size={16} /> : <Bot size={16} />}</span>
      <p>{message.content}</p>
    </article>
  )
}

function ReportPanel({ result }) {
  const report = result?.report
  if (!report) return null
  return (
    <section className="assessment-report-panel">
      <header>
        <span><FileText size={16} /> 成长诊断报告</span>
        <h2>{report.summary || '已生成你的成长报告'}</h2>
      </header>
      <div className="assessment-report-grid">
        <article>
          <strong>推荐方向</strong>
          <p>{report.recommendedDirection || '继续探索'}</p>
        </article>
        <article>
          <strong>当前状态</strong>
          <p>{report.currentState?.major || '专业待补充'} · {report.currentState?.grade || '年级待补充'}</p>
        </article>
      </div>
      <div className="assessment-report-list">
        <article>
          <strong>优势</strong>
          {(report.strengths || []).map((item) => <p key={item}>{item}</p>)}
        </article>
        <article>
          <strong>薄弱点</strong>
          {(report.weakPoints || []).map((item) => <p key={item}>{item}</p>)}
        </article>
        <article>
          <strong>下一步建议</strong>
          {(report.nextSteps || []).map((item) => <p key={item}>{item}</p>)}
        </article>
      </div>
      <div className="assessment-actions">
        <Link className="primary-button" to="/learning">生成个性化路线 <ArrowRight size={16} /></Link>
        <Link className="secondary-button" to="/ai-partner">查看成长画像</Link>
      </div>
    </section>
  )
}

export default function GrowthAssessmentPage() {
  const auth = useAuth()
  const [assessment, setAssessment] = useState(null)
  const [messages, setMessages] = useState([])
  const [result, setResult] = useState(null)
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    setPageMetadata({
      title: 'AI成长诊断 - 青途 AI',
      description: '通过AI引导式成长访谈了解专业、年级、兴趣、目标、困难和能力情况，生成成长报告。',
    })
  }, [])

  const canGenerate = useMemo(() => messages.filter((item) => item.role === 'user').length >= 2, [messages])

  async function startAssessment() {
    if (!auth?.user) return
    setLoading(true)
    setError('')
    try {
      const payload = await createGrowthAssessment()
      setAssessment(payload.assessment)
      setMessages(payload.messages || [])
      setResult(payload.result || null)
    } catch (err) {
      setError(err?.message || '诊断创建失败，请稍后再试')
    } finally {
      setLoading(false)
    }
  }

  async function submitMessage(event) {
    event.preventDefault()
    if (!assessment?.id || !input.trim() || loading) return
    const text = input.trim()
    setInput('')
    setLoading(true)
    setError('')
    setMessages((current) => [...current, { id: `local-${Date.now()}`, role: 'user', content: text }])
    try {
      const payload = await sendGrowthAssessmentMessage(assessment.id, { content: text })
      setAssessment(payload.assessment)
      setMessages(payload.messages || [])
      setResult(payload.result || null)
    } catch (err) {
      setError(err?.message || '消息发送失败，请稍后再试')
    } finally {
      setLoading(false)
    }
  }

  async function createReport() {
    if (!assessment?.id || loading) return
    setLoading(true)
    setError('')
    try {
      const payload = await generateGrowthAssessmentReport(assessment.id)
      setAssessment(payload.assessment)
      setMessages(payload.messages || [])
      setResult(payload.result || null)
    } catch (err) {
      setError(err?.message || '报告生成失败，请稍后再试')
    } finally {
      setLoading(false)
    }
  }

  if (auth?.loading) {
    return (
      <main className="page-container growth-assessment-page">
        <section className="assessment-hero"><Loader2 size={18} /> 正在加载...</section>
      </main>
    )
  }

  if (!auth?.user) {
    return (
      <main className="page-container growth-assessment-page">
        <section className="assessment-hero">
          <span><Sparkles size={16} /> AI成长诊断</span>
          <h1>登录后开始一次成长访谈</h1>
          <p>AI会通过自然聊天了解你的专业、年级、兴趣、目标、困难和能力情况，再生成成长报告。</p>
          <Link className="primary-button" to="/login">去登录 <ArrowRight size={16} /></Link>
        </section>
      </main>
    )
  }

  return (
    <main className="page-container growth-assessment-page">
      <section className="assessment-hero">
        <div>
          <span><Sparkles size={16} /> AI成长诊断 MVP</span>
          <h1>不用填表，像聊天一样梳理你的成长方向</h1>
          <p>我会温和地了解你的专业、年级、学习状态、兴趣、目标、当前困难和能力情况，再把结果同步到成长画像。</p>
        </div>
        {!assessment ? (
          <button className="primary-button" type="button" onClick={startAssessment} disabled={loading}>
            {loading ? <Loader2 size={16} /> : <Bot size={16} />}
            开始诊断
          </button>
        ) : null}
      </section>

      {error ? <p className="student-growth-error">{error}</p> : null}

      {assessment ? (
        <section className="assessment-chat-shell">
          <div className="assessment-chat-list">
            {messages.map((message) => <MessageBubble key={message.id} message={message} />)}
            {loading ? <MessageBubble message={{ id: 'loading', role: 'assistant', content: '我正在整理你的信息...' }} /> : null}
          </div>
          <form className="assessment-chat-form" onSubmit={submitMessage}>
            <textarea
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder="比如：我是大二软件工程，最近想找后端方向，但基础不太稳..."
            />
            <div>
              <button className="secondary-button" type="button" onClick={createReport} disabled={loading || !canGenerate}>
                <FileText size={16} /> 生成成长报告
              </button>
              <button className="primary-button" type="submit" disabled={loading || !input.trim()}>
                <Send size={16} /> 发送
              </button>
            </div>
          </form>
        </section>
      ) : (
        <section className="assessment-empty-panel">
          <Bot size={28} />
          <h2>这不是固定问卷</h2>
          <p>你可以先随便讲一段自己的情况，AI会根据你的回答继续追问，而不是要求你一次填完所有字段。</p>
        </section>
      )}

      <ReportPanel result={result} />
    </main>
  )
}
